import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { OCF_DEFAULT_CORRECTION_REASON } from "features/cdm/constants/correctionReasonOptions";
import { useSyncPatientBaselineFromReview } from "features/cdm/hooks/useSyncPatientBaselineFromReview";
import { toReviewLocalDate } from "features/cdm/utils/reviewDate";
import { toNumberOrUndefined } from "features/cdm/utils/toNumberOrUndefined";

export const useSendOCF = ({ patientId }) => {
  const queryClient = useQueryClient();
  const { syncBaseline } = useSyncPatientBaselineFromReview({ patientId });

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    // Callers surface the real BE message via extractSubmissionError; suppress
    // the generic global mutation toast. 403 is still handled globally.
    meta: { skipGlobalError: true },
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();
      try {
        const response = await axiosInstance.put(
          `${tenantVal}/cdm/patients/${patientId}/ocf/submit`,
          {
            reviewType: data.reviewType,
            reviewDate: toReviewLocalDate(data.reviewDate),
            originalReimbursementMessageControlId:
              data?.reimbursementMessageControlId || null,
            // Reason for Correction (X0339-2) — only on a correction (OCF has a
            // single allowed value); BE emits the X0339 OBX in the CDR message.
            correctionReason: data?.reimbursementMessageControlId
              ? OCF_DEFAULT_CORRECTION_REASON
              : null,
            clinicalData: {
              patientDemographics: {
                ethnicityCode: data.ethnicityCode,
              },
              indications: {
                currentSmokingStatus: data.currentSmokingStatus,
                bmiGreaterThan30: data.bmiGreaterThan30,
                previousHighBnp: data.previousHighBnp,
                highRiskEthnicity: data.highRiskEthnicity,
                historyOfGestationalDiabetes: data.historyOfGestationalDiabetes,
                dyslipidaemia: data.dyslipidaemia,
                chronicKidneyDisease: data.chronicKidneyDisease,
                historyOfSevereMentalIllness: data.historyOfSevereMentalIllness,
                otherText: data.otherText,
              },
              // OCF dataset V1.9 Risk Factors = smoking status, vaping, weight,
              // height, BMI, waist, QRisk3 only. Alcohol, physical activity and
              // all MECC interventions are intentionally dropped (see BE change
              // request: `.claude/cdm-context/ocf-be-change-request-riskfactors.md`).
              riskFactors: {
                smokingStatus: data.smokingStatus,
                vapingStatus: data.vapingStatus,
                weightKg: Number(data.weight),
                heightCm: Number(data.height),
                bmi: Number(data.bmi),
                waistCircumferenceCm: Number(data.waistCircumference),
                qrisk3Score: Number(data.qRisk3Score),
              },
              physicalExam: {
                pulseRate: Number(data.pulse),
                pulseRhythm: data.pulseRhythm,
                systolicBloodPressure: Number(data.bp1),
                diastolicBloodPressure: Number(data.bp2),
              },
              laboratoryInvestigations: {
                haemoglobin: toNumberOrUndefined(data.haemoglobin),
                totalCholesterol: toNumberOrUndefined(data.totalCholesterol),
                hdlCholesterol: toNumberOrUndefined(data.hdlCholesterol),
                nonHdlCholesterol: toNumberOrUndefined(data.nonHdlCholesterol),
                ldlCholesterol: data.ldlCholesterol,
                triglycerides: toNumberOrUndefined(data.triglycerides),
                hba1c: toNumberOrUndefined(data.hba1c),
                serumCreatinine: toNumberOrUndefined(data.serumCreatinine),
                egfr: data.egfr,
                // ACR — see KIN ticket for OCF: BE OcfLaboratoryInvestigationsDto
                // must add `acr` (String). Sent now; ignored until BE adds it.
                acr: data.acr === "Not Available" ? "Not Available" : Number(data.acr) || null,
                // NT-proBNP — hidden until lab capacity is increased (KIN-1050).
                // BE OcfLaboratoryInvestigationsDto must add `ntProBNP`; ignored until then.
                ntProBNP:
                  data.ntProBNP === "Not Available"
                    ? "Not Available"
                    : Number(data.ntProBNP) || null,
              },
            },
            confirmedOutcome: {
              ocfOutcome: data.outcome,
              preventionReasons:
                data.outcome === "REGISTER_ON_PREVENTION_PROGRAMME"
                  ? [
                      data.qrisk3 ? "QRISK_GTE_20" : null,
                      data.hypertenstionStage1
                        ? "HYPERTENSION_STAGE_1_WITH_TARGET_ORGAN_DAMAGE"
                        : null,
                      data.hypertenstionStage1NoTargetOrganDamage
                        ? "HYPERTENSION_STAGE_1_NO_TARGET_ORGAN_DAMAGE"
                        : null,
                      data.hypertenstionStage2or3
                        ? "HYPERTENSION_STAGE_2_OR_3"
                        : null,
                      data.gestationalDiabetes ? "GESTATIONAL_DIABETES" : null,
                      data.preEclampsia ? "PRE_ECLAMPSIA" : null,
                      data.preDiabetes ? "PRE_DIABETES" : null,
                      data.previousBNP ? "PREVIOUS_HIGH_BNP" : null,
                    ].filter(Boolean)
                  : [],
              cdmReasons:
                data.outcome === "DIAGNOSED_WITH_CHRONIC_DISEASE"
                  ? [
                      data.diabetesType2 ? "DIABETES_TYPE_2" : null,
                      data.asthma ? "ASTHMA" : null,
                      data.copd ? "COPD" : null,
                      data.heartFailure ? "HEART_FAILURE" : null,
                      data.atrialFibrillation ? "ATRIAL_FIBRILLATION" : null,
                      data.ischaemicHeartDisease
                        ? "ISCHAEMIC_HEART_DISEASE"
                        : null,
                      data.stroke ? "STROKE" : null,
                      data.tia ? "TIA" : null,
                    ].filter(Boolean)
                  : [],
            },
          }
        );

        return response.data;
      } catch (error) {
        const data = error?.response?.data;

        if (data && typeof data.success === "boolean") {
          return data;
        }

        throw error;
      }
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });

      // OCF mutationFn resolves (instead of throwing) on a BE-reported failure,
      // returning { success: false }. Only sync the baseline on a real success.
      if (response?.success !== false) syncBaseline(variables);
    },
  });
};
